import { ContainerType, ShippingLine } from 'types/Shipping';
import moment from 'moment';
import { Region } from './regions';
// TODO: Verify this dependency cycle is ok
// eslint-disable-next-line
import erlClient from '.';

export enum AvailabilityQueryKey {
  TableData = 'tableData',
  OverwrittenRules = 'overwrittenRules',
  FunctionalParser = 'functionalParser',
  ShippingLineMapping = 'shippingLineMapping',
  GateHours = 'gateHours',
  AvailabilityAppointments = 'availabilityAppointments',
}

export const getData = async (
  shippingLines: ShippingLine[],
  containerTypes: ContainerType[],
  portCollectionCode: Region['code'],
  forDatetime: moment.Moment | undefined,
  signal?: AbortSignal
) => {
  const res = await erlClient.post(
    '/core/api/v1/erl/table',
    {
      shipping_lines: shippingLines,
      container_types: containerTypes,
      port_collection_code: portCollectionCode,
      for_datetime: forDatetime ? forDatetime.format('YYYY-MM-DDTHH:mm:ss') : undefined,
    },
    { signal }
  );
  return res.data;
};

export const getOverwrittenRules = async (
  shippingLines: ShippingLine[],
  containerTypes: ContainerType[],
  portCollectionCode: Region['code'],
  forDatetime: moment.Moment | undefined,
  signal?: AbortSignal
) => {
  const res = await erlClient.post(
    '/core/api/v1/erl/table_overwrite',
    {
      shipping_lines: shippingLines,
      container_types: containerTypes,
      port_collection_code: portCollectionCode,
      for_datetime: forDatetime ? forDatetime.format('YYYY-MM-DDTHH:mm:ss') : undefined,
    },
    { signal }
  );
  return res.data;
};

export function sortOnName<T extends { name: string }>(rows: T[]) {
  return rows.sort((a, b) => {
    const aName = (a.name || '').toUpperCase();
    const bName = (b.name || '').toUpperCase();
    // eslint-disable-next-line no-nested-ternary
    return aName === bName ? 0 : aName < bName ? -1 : 1;
  });
}

export const getShippingLines = async (
  portCollectionCode: Region['code'],
  signal?: AbortSignal
) => {
  const res = await erlClient.get<ShippingLine[]>('/core/api/v1/erl/shipping_line', {
    params: { port_collection_codes: portCollectionCode },
    signal,
  });
  return sortOnName(res.data);
};

export const getContainerTypes = async (signal?: AbortSignal) => {
  const res = await erlClient.get<ContainerType>('/core/api/v1/erl/container_type', { signal });
  return res.data;
};

/** Actually returns terminals :/ */
export const getPorts = async (
  portCollectionCode: Region['code'],
  forDate: moment.Moment | undefined,
  signal?: AbortSignal
) => {
  const res = await erlClient.get('/core/api/v1/erl/port', {
    params: { port_collection_codes: portCollectionCode, for_date: forDate?.format('YYYY-MM-DD') },
    signal,
  });
  return res.data;
};

export const getFunctionalParser = async (
  portCollectionCode: Region['code'],
  forDatetime: moment.Moment | undefined,
  signal?: AbortSignal
) => {
  const res = await erlClient.get('core/api/v1/erl/functional_parsers', {
    params: {
      port_collection_codes: portCollectionCode,
      for_datetime: forDatetime ? forDatetime.format('YYYY-MM-DDTHH:mm:ss') : undefined,
    },
    signal,
  });
  return res.data;
};
export const getShippingLinesMapping = async (
  portCollectionCode: Region['code'],
  forDatetime: moment.Moment | undefined,
  signal?: AbortSignal
) => {
  const res = await erlClient.get('/core/api/v1/erl/mapping_shipping_line', {
    params: {
      port_collection_code: portCollectionCode,
      for_date: forDatetime ? forDatetime.format('YYYY-MM-DD') : undefined,
    },
    signal,
  });
  return res.data;
};

export const getGateHours = async (
  portCollectionCodes: Region['code'][],
  forDatetime: moment.Moment | undefined,
  signal?: AbortSignal
) => {
  const res = await erlClient.get('/core/api/v1/erl/gate_hours', {
    params: {
      port_collection_codes: portCollectionCodes.join(','),
      for_date: forDatetime ? forDatetime.format('YYYY-MM-DD') : undefined,
    },
    signal,
  });
  return res.data;
};

export const getFirstAppointment = async () => {
  const res = await erlClient.get('/core/api/v1/tas/first_appointment');
  return res.data;
};

export const getAvailabilityAppointments = async (
  line: ShippingLine,
  container_type: ContainerType,
  portCollectionCode: Region['code'],
  timezone: string,
  forDatetime: moment.Moment | undefined,
  signal?: AbortSignal
) => {
  try {
    const res = await erlClient.get('/core/api/v1/tas/availability-appointments', {
      params: {
        line,
        container_type,
        port_collection_code: portCollectionCode,
        tz: timezone,
        for_datetime: forDatetime ? forDatetime.format('YYYY-MM-DDTHH:mm:ss') : undefined,
      },
      signal,
    });
    return res.data;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
  }
  return Promise.resolve();
};

export const getAvailabilityCredentialsEnd = async (
  portCollectionCodes: Region['code'][],
  signal?: AbortSignal
) => {
  const res = await erlClient.get('/core/api/v1/appointments/credentials', {
    params: {
      port_collection_codes: portCollectionCodes.join(','),
    },
    signal,
  });
  return res.data;
};
